import {
  Box,
  Flex,
  HStack,
  Text,
} from '@chakra-ui/react';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { MdPersonOutline } from 'react-icons/md';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useModals } from '../../hooks/useModals';
import { ProgressiveBlur } from '../ProgressiveBlur';
import { RiftboundButton } from '../ui/button';
import { RiftboundIconButton } from '../ui/icon-button';
import { GUEST_SIGN_IN_BUTTON_SHORT } from '../../constants/guestExperienceCopy';
import { AccountMenuPopover } from './AccountMenuPopover';
import { BackOrFallbackButton } from './BackOrFallbackButton';
import { SupportKofiHeaderIcon } from './SupportKofiHeaderIcon';
import { CollectionNavSubmenu } from './CollectionNavSubmenu';
import { DeckNavSubmenu } from './DeckNavSubmenu';
import { PlayNavSubmenu } from './PlayNavSubmenu';
import { TeamNavSubmenu } from './TeamNavSubmenu';
import { usePageHeaderNavigation } from './PageHeaderNavigationContext';
import { getMobileNavTitleFallback } from './routeChrome';
import { desktopNavRoutes } from './routeMetadata';
import { useSidebarShell } from './SidebarLayoutContext';
import { useHeaderSearchClaimed, useSetHeaderSearchSlot } from './HeaderSearchSlot';
import { TabletHeaderCatalogSearch } from './TabletHeaderCatalogSearch';
import { glassCircleProps, glassSurfaceProps } from './glass-circle';
import {
  APP_FRAME_MAX_W,
  MOBILE_NAV_COMPACT_TITLE_VAR,
  MOBILE_NAV_MATERIAL_VAR,
  MOBILE_SITE_HEADER_HEIGHT_PX,
  MOBILE_SITE_HEADER_OFFSET_VAR,
  OVERLAY_SCROLL_CSS,
  SAFE_AREA_INSET_LEFT,
  SAFE_AREA_INSET_RIGHT,
  SAFE_AREA_INSET_TOP,
} from './layoutConstants';

function BrandWordmark({ height }: { height: Record<string, string> | string }) {
  return (
    <Box
      as="span"
      display="inline-block"
      h={height}
      aspectRatio={1023 / 189}
      bg="accent"
      flexShrink={0}
      css={{
        WebkitMaskImage: `url(${import.meta.env.BASE_URL}riftbound-db-logo.png)`,
        maskImage: `url(${import.meta.env.BASE_URL}riftbound-db-logo.png)`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      }}
    />
  );
}

/** Outline when the filter column is hidden; the rail fills when it is open. */
function SidebarToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {open ? (
        <path
          d="M5 3.75h4v16.5H5a1.75 1.75 0 0 1-1.75-1.75V5.5A1.75 1.75 0 0 1 5 3.75Z"
          fill="currentColor"
          stroke="none"
        />
      ) : null}
      <rect x="3" y="3" width="18" height="18" rx="2" />
      {open ? null : <path d="M9 3v18" />}
    </svg>
  );
}

function MobileGlassPill({ children }: { children: ReactNode }) {
  return (
    <HStack
      gap={0.5}
      px={1.5}
      py={0.5}
      borderRadius="full"
      {...glassSurfaceProps}
    >
      {children}
    </HStack>
  );
}

/** Mobile Ko-fi + account (or sign-in) — stays put; large title clips under it. */
function MobileAccountPill({
  user,
  onSignIn,
}: {
  user: ReturnType<typeof useAuth>['user'];
  onSignIn: () => void;
}) {
  if (!user) {
    return (
      <RiftboundIconButton
        aria-label={GUEST_SIGN_IN_BUTTON_SHORT}
        variant="ghost"
        size="sm"
        color="text"
        onClick={onSignIn}
        {...glassCircleProps}
      >
        <MdPersonOutline size={22} />
      </RiftboundIconButton>
    );
  }

  return (
    <MobileGlassPill>
      <SupportKofiHeaderIcon />
      <AccountMenuPopover />
    </MobileGlassPill>
  );
}

export function AppHeader() {
  const location = useLocation();
  const { user } = useAuth();
  const { openSignIn } = useModals();
  const pageHeaderNavigation = usePageHeaderNavigation();
  const headerRef = useRef<HTMLElement | null>(null);
  const mobileBack = pageHeaderNavigation?.mobileBack;
  const mobileTitle =
    pageHeaderNavigation?.mobileTitle ?? getMobileNavTitleFallback(location.pathname);
  const showMobileBrand = location.pathname === '/' || !mobileTitle;

  const visibleNav = desktopNavRoutes();
  const sidebarShell = useSidebarShell();
  const showFilterToggle = Boolean(sidebarShell?.enabled);
  const setHeaderSearchSlot = useSetHeaderSearchSlot();
  const headerSearchClaimed = useHeaderSearchClaimed();

  // Publish real header height so sticky sub-bars (SearchStats, etc.) sit flush below it.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;

    const root = document.documentElement;
    const sync = () => {
      const height = el.getBoundingClientRect().height;
      if (height > 0) {
        root.style.setProperty(MOBILE_SITE_HEADER_OFFSET_VAR, `${height}px`);
      }
    };

    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(MOBILE_SITE_HEADER_OFFSET_VAR);
    };
  }, []);

  return (
    <Box
      as="header"
      ref={headerRef}
      data-app-header=""
      position={{ base: 'fixed', md: 'sticky' }}
      top={0}
      left={{ base: 0, md: 'auto' }}
      right={{ base: 0, md: 'auto' }}
      w="100%"
      zIndex={50}
      bg={{ base: 'transparent', md: 'bgElevated' }}
      borderBottomWidth={{ base: 0, md: 'hairline' }}
      borderColor="border"
      // Desktop nav submenus render inside the header and must overflow it.
      // Mobile clips the frost to this box, which already starts at the top
      // of the screen and includes the status bar.
      overflow={{ base: 'hidden', md: 'visible' }}
      css={{
        '@media screen and (max-width: 47.99em)': {
          backgroundColor: `color-mix(in srgb, var(--chakra-colors-bg) calc(var(${MOBILE_NAV_MATERIAL_VAR}, 1) * 45%), transparent)`,
        },
      }}
    >
      {/* One frost from the top of the screen down. Content is padded below the status bar. */}
      <ProgressiveBlur
        display={{ base: 'block', md: 'none' }}
        edge="top"
        blur={36}
        bg="bg/28"
        top={0}
        bottom={0}
        h="auto"
        zIndex={0}
        style={{ opacity: `var(${MOBILE_NAV_MATERIAL_VAR}, 1)` }}
        css={{
          maskImage:
            'linear-gradient(to bottom, black 0%, black 48%, rgba(0,0,0,0.75) 72%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, black 0%, black 48%, rgba(0,0,0,0.75) 72%, transparent 100%)',
        }}
      />

      <Flex
        display={{ base: 'grid', md: 'flex' }}
        gridTemplateColumns={{ base: 'minmax(44px, 1fr) minmax(0, 2.5fr) minmax(44px, 1fr)', md: undefined }}
        maxW={APP_FRAME_MAX_W}
        mx="auto"
        align="center"
        justify="space-between"
        pl={{ base: `calc(1rem + ${SAFE_AREA_INSET_LEFT})`, xl: 6 }}
        pr={{ base: `calc(1rem + ${SAFE_AREA_INSET_RIGHT})`, xl: 6 }}
        pt={{ base: `calc(0.75rem + ${SAFE_AREA_INSET_TOP})`, md: 3 }}
        pb={3}
        gap={{ base: 4, md: 2, xl: 4 }}
        minH={{
          base: `calc(${MOBILE_SITE_HEADER_HEIGHT_PX}px + ${SAFE_AREA_INSET_TOP})`,
          md: `${MOBILE_SITE_HEADER_HEIGHT_PX}px`,
        }}
        position="relative"
        zIndex={1}
      >
        {/* Tablet card search. Catalog pages portal their own filter field into this slot. */}
        <Box
          ref={setHeaderSearchSlot}
          display={{ base: 'none', md: 'block', lg: 'none' }}
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%, -50%)"
          w="min(24rem, calc(100% - 26rem))"
          zIndex={2}
          pointerEvents="none"
          css={{ '&:not(:empty)': { pointerEvents: 'auto' } }}
        >
          {headerSearchClaimed ? null : <TabletHeaderCatalogSearch />}
        </Box>
        <HStack display={{ base: 'flex', md: 'none' }} justify="flex-start" minW={0}>
          {mobileBack ? (
            <BackOrFallbackButton
              fallback={mobileBack.fallback}
              label={mobileBack.label}
              ariaLabel={mobileBack.ariaLabel ?? 'Back'}
              onBeforeNavigate={mobileBack.onBeforeNavigate}
              iconOnly
              color="text"
              {...glassCircleProps}
            />
          ) : null}
        </HStack>

        {/* Stage 3: compact title — clipped to the end of the tuck range. */}
        <HStack
          gap={2}
          flex={{ md: '1' }}
          flexShrink={1}
          minW={0}
          justifySelf={{ base: 'center', md: 'flex-start' }}
          justify={{ base: 'center', md: 'flex-start' }}
          maxW="100%"
        >
          {showFilterToggle ? (
            <RiftboundIconButton
              id="catalog-filter-toggle"
              aria-label={sidebarShell?.collapsed ? 'Show filters' : 'Hide filters'}
              aria-expanded={!sidebarShell?.collapsed}
              aria-controls="catalog-filter-column"
              variant="ghost"
              size="sm"
              display={{ base: 'none', md: 'inline-flex' }}
              flexShrink={0}
              color={sidebarShell?.collapsed ? 'text' : 'accent'}
              bg="transparent"
              _hover={{ bg: 'transparent', color: sidebarShell?.collapsed ? 'text' : 'accent' }}
              _active={{ bg: 'transparent', color: sidebarShell?.collapsed ? 'text' : 'accent' }}
              onClick={sidebarShell?.toggleCollapsed}
            >
              <SidebarToggleIcon open={!sidebarShell?.collapsed} />
            </RiftboundIconButton>
          ) : null}
          <Box display={{ base: showMobileBrand ? 'block' : 'none', md: 'block' }} flexShrink={0}>
            <RouterLink to="/" aria-label="Riftbound DB home">
              <BrandWordmark height={{ base: '22px', sm: '26px', md: '22px', xl: '28px' }} />
            </RouterLink>
          </Box>
          {!showMobileBrand && mobileTitle ? (
            <Text
              display={{ base: 'block', md: 'none' }}
              as="p"
              fontFamily="heading"
              fontWeight="semibold"
              fontSize="17px"
              lineHeight="1.2"
              color="text"
              textAlign="center"
              truncate
              maxW="100%"
              px={1}
              style={{
                opacity: `var(${MOBILE_NAV_COMPACT_TITLE_VAR}, 1)`,
                transformOrigin: 'center center',
                transform: `translateY(calc((1 - var(${MOBILE_NAV_COMPACT_TITLE_VAR}, 1)) * 6px))`,
                filter: `blur(calc((1 - var(${MOBILE_NAV_COMPACT_TITLE_VAR}, 1)) * 8px))`,
                willChange: 'opacity, transform, filter',
              }}
            >
              {mobileTitle}
            </Text>
          ) : null}

          {/* Header links from `lg`. Tablet uses the floating bottom bar. */}
          <HStack
            gap={{ md: 0.5, xl: 1 }}
            display={{ base: 'none', lg: 'flex' }}
            ml={{ md: 2, xl: 4 }}
            minW={0}
            flex="1 1 auto"
            overflowX="auto"
            css={{
              ...OVERLAY_SCROLL_CSS,
              '& > *': { flexShrink: 0 },
            }}
          >
            {visibleNav.map((item) => {
              if (item.to === '/collection' && item.match) {
                return <CollectionNavSubmenu key={item.to} navMatch={item.match} />;
              }
              if (item.to === '/decks' && item.match) {
                return <DeckNavSubmenu key={item.to} navMatch={item.match} />;
              }
              if (item.to === '/play' && item.match) {
                return <PlayNavSubmenu key={item.to} navMatch={item.match} />;
              }
              if (item.to === '/team' && item.match) {
                return <TeamNavSubmenu key={item.to} navMatch={item.match} />;
              }
              const active = item.match
                ? item.match(location.pathname)
                : location.pathname === item.to ||
                  location.pathname.startsWith(`${item.to}/`);
              return (
                <RiftboundButton
                  key={item.to}
                  as={RouterLink as never}
                  {...{ to: item.to } as Record<string, unknown>}
                  treatment="ghost"
                  size="sm"
                  variant={active ? 'primary' : undefined}
                  px={{ md: 2, xl: 3 }}
                  h="auto"
                  minH={0}
                  py={1.5}
                  lineHeight={1.15}
                >
                  {item.label}
                </RiftboundButton>
              );
            })}
          </HStack>
        </HStack>

        <HStack gap={2} flexShrink={0} justifySelf={{ base: 'end', md: 'auto' }}>
          <Box display={{ base: 'block', md: 'none' }}>
            <MobileAccountPill user={user} onSignIn={openSignIn} />
          </Box>
          <HStack gap={2} display={{ base: 'none', md: 'flex' }}>
            {user ? (
              <>
                <SupportKofiHeaderIcon />
                <AccountMenuPopover />
              </>
            ) : (
              <RiftboundButton treatment="primary" size="sm" onClick={openSignIn}>
                {GUEST_SIGN_IN_BUTTON_SHORT}
              </RiftboundButton>
            )}
          </HStack>
        </HStack>
      </Flex>
    </Box>
  );
}
